import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Container,
  Card,
  Heading,
  Table,
  Badge,
  TextField,
  Separator
} from '@radix-ui/themes';
import {
  ReloadIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  QuestionMarkCircledIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon
} from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { organizationsAdminApi, schedulerApi, schedulerTestApi, surveyTemplatesApi } from '../services/api';
import { formatInTimeZone } from 'date-fns-tz';
import { formatFrequency, humanizeCron, format12h } from '../utils/frequencyUtils';
import TimeInput12h from './common/TimeInput12h';
import cronstrue from 'cronstrue';

interface SchedulerItem {
  item_type: 'survey' | 'task';
  occurrence_id?: number | null;
  patient_id: string;
  patient_name: string;
  first_name?: string;
  last_name?: string;
  item_name: string;
  survey_name?: string;
  task?: string;
  disease?: string;
  scheduled_time: string;
  release_at?: string;
  due_at: string;
  close_at: string;
  status: 'pending' | 'released' | 'completed_on_time' | 'completed_late' | 'missed' | 'predicted';
  frequency: string;
  timezone?: string;
  survey_id?: string;
  assignee?: string;
}

interface DaySummary {
  day: string;
  total_count: number;
  surveys_count: number;
  tasks_count: number;
  pending_count: number;
  released_count: number;
  completed_count: number;
  missed_count: number;
}

interface OrganizationContactExclusions {
  id: number;
  owner: string | null;
  name: string | null;
  patient_contact_excluded_weekdays: string[] | null;
}

interface OrganizationExcludedDateRow {
  organization: number;
  exclusion: string;
  reason: string | null;
}

interface EligiblePatientSurvey {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  preferred_contact_method: string | null;
  survey_id: string;
  survey_name: string;
  frequency: string;
  organization: number | null;
  voice_enabled: boolean;
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const AdminScheduler: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<SchedulerItem[]>([]);
  const [summary, setSummary] = useState<DaySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Organization contact exclusions (admin)
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgs, setOrgs] = useState<OrganizationContactExclusions[]>([]);
  const [expandedOrgIds, setExpandedOrgIds] = useState<Set<number>>(new Set());
  const [excludedDatesByOrg, setExcludedDatesByOrg] = useState<Record<number, OrganizationExcludedDateRow[]>>({});
  const [excludedDatesLoadingByOrg, setExcludedDatesLoadingByOrg] = useState<Record<number, boolean>>({});
  const [weekdaysDraftByOrg, setWeekdaysDraftByOrg] = useState<Record<number, string[]>>({});
  const [addExcludedDateDraftByOrg, setAddExcludedDateDraftByOrg] = useState<Record<number, { exclusion: string; reason: string }>>({});

  // Test trigger states
  const [arbitraryNow, setArbitraryNow] = useState<string>('');
  const [newArbitraryNow, setNewArbitraryNow] = useState<string>('');
  const [arbitraryNowLoading, setArbitraryNowLoading] = useState(false);
  // PHA-1121: a non-empty ARBITRARY_NOW freezes the shared cron scheduler, not just
  // this page's one-off trigger. Surfaced as a warning so it cannot be left on unnoticed.
  const schedulerClockFrozen = Boolean(arbitraryNow && arbitraryNow.trim());
  const [eligiblePatients, setEligiblePatients] = useState<EligiblePatientSurvey[]>([]);
  const [eligiblePatientsLoading, setEligiblePatientsLoading] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [phoneOverride, setPhoneOverride] = useState<string>('');
  const [contactMethod, setContactMethod] = useState<'sms' | 'voice'>('sms');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState<any>(null);

  // PHA-406 V2: Survey Templates state
  const [templatesOrgId, setTemplatesOrgId] = useState<string | null>(null);
  const [templatesDraft, setTemplatesDraft] = useState<any[]>([]);
  const [templatesOriginal, setTemplatesOriginal] = useState<any[]>([]);
  const [unscheduledGroups, setUnscheduledGroups] = useState<string[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);

  // Filter states
  const [dateRange, setDateRange] = useState<'today' | 'week' | '2weeks' | '7days' | 'custom'>('7days');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [itemTypes, setItemTypes] = useState<string[]>(['survey', 'task']);
  const [statusFilter, setStatusFilter] = useState<string[]>(['pending', 'released', 'completed_on_time', 'completed_late', 'missed']);
  
  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [nextRefresh, setNextRefresh] = useState<number>(300); // 5 minutes in seconds

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    
    switch (dateRange) {
      case 'today':
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        end.setDate(end.getDate() + 7);
        break;
      case '2weeks':
        end.setDate(end.getDate() + 14);
        break;
      case '7days':
        end.setDate(end.getDate() + 7);
        break;
      case 'custom':
        if (startDate && endDate) {
          return {
            start_date: new Date(startDate).toISOString(),
            end_date: new Date(endDate).toISOString()
          };
        }
        // Fallback to 7 days if custom dates not set
        end.setDate(end.getDate() + 7);
        break;
    }
    
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  }, [dateRange, startDate, endDate]);

  // Load scheduler data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const dates = getDateRange();
      const filters = {
        ...dates,
        item_types: itemTypes.length > 0 ? itemTypes : undefined,
        status_filter: statusFilter.length > 0 ? statusFilter : undefined
      };
      
      console.log('📅 Loading scheduler data with filters:', filters);
      
      // Load both items and summary in parallel
      const [itemsData, summaryData] = await Promise.all([
        schedulerApi.getUpcomingItems(filters),
        schedulerApi.getSummary(filters)
      ]);
      
      console.log('✅ Received scheduler data:', { itemsData, summaryData });
      console.log('🔍 Full first record:', itemsData.records?.[0]);
      
      // Parse and format items
      const formattedItems: SchedulerItem[] = (itemsData.records || []).map((record: any) => {
        console.log('📋 Full record:', record);
        const patientName = `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'Unknown Patient';
        const itemName = record.survey_name || record.task || '';
        const scheduledTime = record.release_at || record.due_at;
        
        return {
          item_type: record.survey_id ? 'survey' : 'task',
          occurrence_id: record.occurrence_id,
          patient_id: record.patient_id,
          patient_name: patientName,
          first_name: record.first_name,
          last_name: record.last_name,
          item_name: itemName,
          survey_name: record.survey_name,
          task: record.task,
          disease: record.disease,
          scheduled_time: scheduledTime,
          release_at: record.release_at,
          due_at: record.due_at,
          close_at: record.close_at,
          status: record.status || 'predicted',
          frequency: record.frequency,
          timezone: record.timezone,
          survey_id: record.survey_id,
          assignee: record.assignee
        };
      });
      
      setItems(formattedItems);
      setSummary(summaryData.records || []);
      setLastRefresh(new Date());
      setNextRefresh(300);
      
    } catch (err: any) {
      console.error('❌ Failed to load scheduler data:', err);
      setError(err.message || 'Failed to load scheduler data');
      toast.error('Failed to load scheduler data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, itemTypes, statusFilter]);

  const loadOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const data = await organizationsAdminApi.listContactExclusions();
      const orgRecords: OrganizationContactExclusions[] = (data.records || []).map((r: any) => ({
        id: Number(r.id),
        owner: r.owner ?? null,
        name: r.name ?? null,
        patient_contact_excluded_weekdays: r.patient_contact_excluded_weekdays ?? null,
      }));
      setOrgs(orgRecords);
      // seed drafts from DB values
      const nextDrafts: Record<number, string[]> = {};
      for (const o of orgRecords) {
        nextDrafts[o.id] = Array.isArray(o.patient_contact_excluded_weekdays) ? [...o.patient_contact_excluded_weekdays] : [];
      }
      setWeekdaysDraftByOrg(nextDrafts);
    } catch (err: any) {
      console.error('❌ Failed to load organizations contact exclusions:', err);
      toast.error(err.message || 'Failed to load organizations');
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  // PHA-406 V2: Load survey templates for a selected org
  const loadSurveyTemplates = useCallback(async (orgId: string) => {
    setTemplatesOrgId(orgId);
    setTemplatesLoading(true);
    try {
      const data = await surveyTemplatesApi.getTemplates(orgId);
      setTemplatesDraft(data.templates || []);
      setTemplatesOriginal(JSON.parse(JSON.stringify(data.templates || [])));
      setUnscheduledGroups(data.unscheduled_groups || []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load survey templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // PHA-406 V2: Save all templates
  const saveSurveyTemplates = useCallback(async () => {
    if (!templatesOrgId) return;
    setTemplatesSaving(true);
    try {
      await surveyTemplatesApi.saveTemplates(templatesOrgId, templatesDraft);
      setTemplatesOriginal(JSON.parse(JSON.stringify(templatesDraft)));
      // Recompute unscheduled
      const allGroups = ['vitals', 'medication', 'diet', 'activity', 'hospitalization', 'symptoms', 'voice of the patient'];
      const assigned = new Set(templatesDraft.flatMap((t: any) => t.question_groups || []));
      setUnscheduledGroups(allGroups.filter(g => !assigned.has(g)));
    } catch (err: any) {
      toast.error(err.message || 'Failed to save survey templates');
    } finally {
      setTemplatesSaving(false);
    }
  }, [templatesOrgId, templatesDraft]);

  const templatesEqual = (a: any[], b: any[]) => {
    const normalize = (templates: any[]) => templates.map(t => ({
      ...t,
      question_groups: [...(t.question_groups || [])].sort()
    }));
    return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
  };

  const DAY_NAMES_SHORT: Record<number, string> = {0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat'};

  const getTemplateTitle = (tmpl: any) => {
    const time = format12h(tmpl.cadence_time_hour ?? 9, tmpl.cadence_time_minute ?? 0);
    if (tmpl.cadence_type === 'daily') return `Daily ${time} Plan Check-In`;
    if (tmpl.cadence_type === 'weekly') return `Weekly ${DAY_NAMES_SHORT[tmpl.cadence_day ?? 1]} ${time} Plan Check-In`;
    return 'New Survey';
  };

  const QUESTION_GROUP_LABELS: Record<string, string> = {
    vitals: 'Vitals & Objective Measurements',
    medication: 'Medication Adherence',
    diet: 'Diet Adherence',
    activity: 'Physical Activity',
    hospitalization: 'Hospitalization & ER Visit',
    symptoms: 'Symptoms & Condition Monitoring',
    'voice of the patient': 'Voice of the Patient',
  };
  const ALL_GROUPS = ['vitals', 'medication', 'diet', 'activity', 'hospitalization', 'symptoms', 'voice of the patient'];

  const toggleOrgExpanded = async (orgId: number) => {
    setExpandedOrgIds(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });

    // Lazy-load excluded dates when expanding
    if (!excludedDatesByOrg[orgId] && !excludedDatesLoadingByOrg[orgId]) {
      setExcludedDatesLoadingByOrg(prev => ({ ...prev, [orgId]: true }));
      try {
        const data = await organizationsAdminApi.listExcludedDates(orgId);
        setExcludedDatesByOrg(prev => ({ ...prev, [orgId]: (data.records || []) as OrganizationExcludedDateRow[] }));
      } catch (err: any) {
        console.error('❌ Failed to load excluded dates:', err);
        toast.error(err.message || 'Failed to load excluded dates');
      } finally {
        setExcludedDatesLoadingByOrg(prev => ({ ...prev, [orgId]: false }));
      }
    }
  };

  const saveWeekdays = async (orgId: number) => {
    try {
      const weekdays = weekdaysDraftByOrg[orgId] || [];
      const res = await organizationsAdminApi.updateExcludedWeekdays(orgId, weekdays);
      if (res?.updated) {
        toast.success('Weekdays updated');
        // reflect in org list
        setOrgs(prev => prev.map(o => (o.id === orgId ? { ...o, patient_contact_excluded_weekdays: res.patient_contact_excluded_weekdays ?? weekdays } : o)));
      } else {
        toast.error('Update did not apply (org may be inactive)');
      }
    } catch (err: any) {
      console.error('❌ Failed to update weekdays:', err);
      toast.error(err.message || 'Failed to update weekdays');
    }
  };

  const addExcludedDate = async (orgId: number) => {
    const draft = addExcludedDateDraftByOrg[orgId] || { exclusion: '', reason: '' };
    if (!draft.exclusion.trim()) {
      toast.error('Exclusion is required');
      return;
    }
    try {
      const res = await organizationsAdminApi.addExcludedDate(orgId, { exclusion: draft.exclusion, reason: draft.reason || null });
      if (res?.inserted) {
        toast.success('Excluded date added');
        // refresh list for org
        const data = await organizationsAdminApi.listExcludedDates(orgId);
        setExcludedDatesByOrg(prev => ({ ...prev, [orgId]: (data.records || []) as OrganizationExcludedDateRow[] }));
        setAddExcludedDateDraftByOrg(prev => ({ ...prev, [orgId]: { exclusion: '', reason: '' } }));
      } else {
        toast.error('Insert did not apply');
      }
    } catch (err: any) {
      console.error('❌ Failed to add excluded date:', err);
      toast.error(err.message || 'Failed to add excluded date');
    }
  };

  const deleteExcludedDate = async (orgId: number, exclusion: string) => {
    try {
      const res = await organizationsAdminApi.deleteExcludedDate(orgId, exclusion);
      if (res?.deleted) {
        toast.success('Excluded date removed');
        setExcludedDatesByOrg(prev => ({
          ...prev,
          [orgId]: (prev[orgId] || []).filter(r => r.exclusion !== exclusion),
        }));
      } else {
        toast.error('Delete did not apply');
      }
    } catch (err: any) {
      console.error('❌ Failed to delete excluded date:', err);
      toast.error(err.message || 'Failed to delete excluded date');
    }
  };

  // Test trigger functions
  const loadArbitraryNow = useCallback(async () => {
    setArbitraryNowLoading(true);
    try {
      const data = await schedulerTestApi.getArbitraryNow();
      setArbitraryNow(data.value || '');
      if (!newArbitraryNow) setNewArbitraryNow(data.value || '');
    } catch (err: any) {
      console.error('Failed to load ARBITRARY_NOW:', err);
    } finally {
      setArbitraryNowLoading(false);
    }
  }, [newArbitraryNow]);

  const updateArbitraryNow = async () => {
    if (!newArbitraryNow) return;
    setArbitraryNowLoading(true);
    try {
      const data = await schedulerTestApi.setArbitraryNow(newArbitraryNow);
      if (data.success) {
        setArbitraryNow(data.value);
        toast.success('ARBITRARY_NOW updated');
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update ARBITRARY_NOW');
    } finally {
      setArbitraryNowLoading(false);
    }
  };

  // PHA-1121: hand the shared scheduler clock back to real time. Blanks the value
  // rather than deleting the variable — the scheduler reads it every cycle and a
  // deleted one makes that read fail.
  const clearArbitraryNow = async () => {
    setArbitraryNowLoading(true);
    try {
      const data = await schedulerTestApi.setArbitraryNow('');
      if (data.success) {
        setArbitraryNow('');
        setNewArbitraryNow('');
        toast.success('Scheduler is back on the real clock');
      } else {
        toast.error(data.error || 'Failed to clear ARBITRARY_NOW');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to clear ARBITRARY_NOW');
    } finally {
      setArbitraryNowLoading(false);
    }
  };

  const loadEligiblePatients = useCallback(async () => {
    setEligiblePatientsLoading(true);
    try {
      const data = await schedulerTestApi.getEligiblePatients();
      setEligiblePatients(data.records || []);
    } catch (err: any) {
      console.error('Failed to load eligible patients:', err);
    } finally {
      setEligiblePatientsLoading(false);
    }
  }, []);

  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    setSelectedSurveyId('');
    setTriggerResult(null);
    const patient = eligiblePatients.find(p => p.patient_id === patientId);
    if (patient) {
      setPhoneOverride(patient.phone_number || '');
      // PHA-1309: a care_team_call patient has no automated channel; a manual trigger defaults to SMS.
      setContactMethod(patient.preferred_contact_method === 'voice' ? 'voice' : 'sms');
    }
  };

  const handleTrigger = async () => {
    if (!selectedPatientId || !selectedSurveyId || !newArbitraryNow) {
      toast.error('Please select a patient, survey, and set ARBITRARY_NOW');
      return;
    }
    setTriggerLoading(true);
    setTriggerResult(null);
    try {
      const result = await schedulerTestApi.triggerSurvey({
        patient_id: selectedPatientId,
        survey_id: selectedSurveyId,
        arbitrary_now: newArbitraryNow,
        phone_number_override: phoneOverride || undefined,
        contact_method: contactMethod,
      });
      setTriggerResult(result);
      if (result.success) {
        toast.success('Survey triggered successfully');
      } else {
        toast.error(result.error || 'Trigger failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to trigger survey');
      setTriggerResult({ success: false, error: err.message });
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleClearClaimAndRetry = async () => {
    if (!triggerResult?.error) return;
    const match = triggerResult.error.match(/occurrence (\d+)/);
    if (!match) return;
    const occurrenceId = parseInt(match[1], 10);
    setTriggerLoading(true);
    try {
      await schedulerTestApi.clearClaim(occurrenceId);
      toast.success(`Cleared claim on occurrence ${occurrenceId}`);
      setTriggerResult(null);
      await handleTrigger();
    } catch (err: any) {
      toast.error(err.message || 'Failed to clear claim');
      setTriggerLoading(false);
    }
  };

  // Derived: unique patients for dropdown
  const uniquePatients = React.useMemo(() => {
    const seen = new Set<string>();
    return eligiblePatients.filter(p => {
      if (seen.has(p.patient_id)) return false;
      seen.add(p.patient_id);
      return true;
    });
  }, [eligiblePatients]);

  // Derived: surveys for selected patient
  const patientSurveys = React.useMemo(() => {
    return eligiblePatients.filter(p => p.patient_id === selectedPatientId);
  }, [eligiblePatients, selectedPatientId]);

  // Initial load
  useEffect(() => {
    loadData();
    loadOrganizations();
    loadArbitraryNow();
    loadEligiblePatients();
  }, [loadData, loadOrganizations, loadArbitraryNow, loadEligiblePatients]);

  // PHA-406 V2: Auto-select first org for survey templates once orgs are loaded
  useEffect(() => {
    if (orgs.length > 0 && !templatesOrgId) {
      loadSurveyTemplates(String(orgs[0].id));
    }
  }, [orgs, templatesOrgId, loadSurveyTemplates]);

  // Auto-refresh timer
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      setNextRefresh(prev => {
        if (prev <= 1) {
          loadData();
          return 300;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  // Format date/time with timezone
  const formatDateTime = (dateString: string, timezone?: string) => {
    if (!dateString) return 'N/A';
    const tz = timezone || 'UTC';
    try {
      return formatInTimeZone(new Date(dateString), tz, 'MMM d, h:mm a');
    } catch (error) {
      console.error('Error formatting date with timezone:', error);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(dateString));
    }
  };

  // Get status badge color
  const getStatusColor = (status: string): 'green' | 'blue' | 'orange' | 'red' | 'gray' => {
    switch (status) {
      case 'completed_on_time':
      case 'completed_late':
        return 'green';
      case 'released':
        return 'blue';
      case 'pending':
      case 'predicted':
        return 'orange';
      case 'missed':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed_on_time':
      case 'completed_late':
        return <CheckCircledIcon />;
      case 'missed':
        return <CrossCircledIcon />;
      case 'predicted':
        return <QuestionMarkCircledIcon />;
      default:
        return <ClockIcon />;
    }
  };

  // Calculate summary totals
  const totalSummary = summary.reduce((acc, day) => ({
    total: acc.total + day.total_count,
    surveys: acc.surveys + day.surveys_count,
    tasks: acc.tasks + day.tasks_count,
    pending: acc.pending + day.pending_count,
    released: acc.released + day.released_count,
    completed: acc.completed + day.completed_count,
    missed: acc.missed + day.missed_count
  }), { total: 0, surveys: 0, tasks: 0, pending: 0, released: 0, completed: 0, missed: 0 });

  // Toggle item type filter
  const toggleItemType = (type: string) => {
    setItemTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Toggle status filter
  const toggleStatus = (status: string) => {
    setStatusFilter(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  return (
    <Box className="min-h-screen bg-slate-1">
      {/* Header */}
      <Box className="border-b border-slate-6 bg-white">
        <Container size="4">
          <Flex align="center" justify="between" py="3">
            <Flex align="center" gap="3">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeftIcon />
              </Button>
              <Heading size="6" className="text-slate-800">
                Admin Scheduler
              </Heading>
            </Flex>
            <Flex align="center" gap="3">
              <Text size="2" className="text-slate-600">
                Last updated: {formatDateTime(lastRefresh.toISOString())}
              </Text>
              {autoRefresh && (
                <Text size="2" className="text-slate-600">
                  Next refresh: {Math.floor(nextRefresh / 60)}:{(nextRefresh % 60).toString().padStart(2, '0')}
                </Text>
              )}
              <Button
                variant="soft"
                onClick={() => setAutoRefresh(!autoRefresh)}
                color={autoRefresh ? 'green' : 'gray'}
              >
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </Button>
              <Button
                variant="solid"
                onClick={loadData}
                disabled={loading}
              >
                <ReloadIcon className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Container size="4" className="py-6">
        {/* Test Survey Trigger */}
        <Card className="mb-6">
          <Box p="4">
            <Heading size="4" className="mb-4">Test Survey Trigger</Heading>
            <Separator className="mb-4" />

            {/* ARBITRARY_NOW */}
            <Flex direction="column" gap="2" className="mb-4">
              <Text size="2" weight="bold">ARBITRARY_NOW (Simulated Time)</Text>
              <Flex align="center" gap="2">
                <Text size="2" className="text-slate-600">
                  Current: {arbitraryNow || '(not set — scheduler is on the real clock)'}
                </Text>
                <Button variant="ghost" size="1" onClick={loadArbitraryNow} disabled={arbitraryNowLoading}>
                  <ReloadIcon className={arbitraryNowLoading ? 'animate-spin' : ''} />
                </Button>
              </Flex>
              {/*
                PHA-1121: this setting is shared and affects the 15-minute cron, not just
                the one-off trigger below. Left set, the scheduler runs as if it were that
                instant forever and releases nothing scheduled after it — which is exactly
                how it silently sat frozen on a past Tuesday while surveys were "not firing".
              */}
              {schedulerClockFrozen && (
                <Box className="rounded border border-amber-300 bg-amber-50 p-2">
                  <Text size="2" as="p" className="text-amber-900">
                    <strong>The scheduler clock is frozen at {arbitraryNow}.</strong> This is
                    shared — the 15-minute scheduler is running as if it were that moment, so
                    nothing scheduled after it will be sent to anyone until you clear it.
                  </Text>
                  <Button
                    variant="solid"
                    color="amber"
                    size="1"
                    className="mt-2"
                    onClick={clearArbitraryNow}
                    disabled={arbitraryNowLoading}
                  >
                    Use real time
                  </Button>
                </Box>
              )}
              <Flex align="center" gap="2">
                <TextField.Root style={{ flex: 1 }}>
                  <TextField.Input
                    type="datetime-local"
                    value={newArbitraryNow ? newArbitraryNow.replace(' ', 'T').slice(0, 16) : ''}
                    onChange={(e) => setNewArbitraryNow(e.currentTarget.value ? e.currentTarget.value.replace('T', 'T') : '')}
                  />
                </TextField.Root>
                <Button variant="solid" onClick={updateArbitraryNow} disabled={arbitraryNowLoading || !newArbitraryNow}>
                  Update
                </Button>
              </Flex>
            </Flex>

            <Separator className="mb-4" />

            {/* Patient Selection */}
            <Flex direction="column" gap="2" className="mb-4">
              <Flex align="center" gap="2">
                <Text size="2" weight="bold">Patient</Text>
                <Button variant="ghost" size="1" onClick={loadEligiblePatients} disabled={eligiblePatientsLoading}>
                  <ReloadIcon className={eligiblePatientsLoading ? 'animate-spin' : ''} />
                </Button>
              </Flex>
              <select
                value={selectedPatientId}
                onChange={(e) => handlePatientSelect(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--gray-7)',
                  fontSize: '14px',
                  backgroundColor: 'white',
                }}
              >
                <option value="">-- Select a patient --</option>
                {uniquePatients.map(p => (
                  <option key={p.patient_id} value={p.patient_id}>
                    {p.patient_id} - {p.first_name || ''} {p.last_name || ''} ({p.phone_number || 'no phone'})
                  </option>
                ))}
              </select>
            </Flex>

            {/* Survey Selection */}
            {selectedPatientId && (
              <Flex direction="column" gap="2" className="mb-4">
                <Text size="2" weight="bold">Survey</Text>
                <select
                  value={selectedSurveyId}
                  onChange={(e) => { setSelectedSurveyId(e.target.value); setTriggerResult(null); }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--gray-7)',
                    fontSize: '14px',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="">-- Select a survey --</option>
                  {patientSurveys.map(s => (
                    <option key={s.survey_id} value={s.survey_id}>
                      {s.survey_name} ({s.frequency})
                    </option>
                  ))}
                </select>
              </Flex>
            )}

            {/* Phone Override & Contact Method */}
            {selectedPatientId && selectedSurveyId && (
              <>
                <Flex direction="column" gap="2" className="mb-4">
                  <Text size="2" weight="bold">Phone Number Override</Text>
                  <TextField.Root>
                    <TextField.Input
                      placeholder="Leave blank to use patient's stored phone"
                      value={phoneOverride}
                      onChange={(e) => setPhoneOverride(e.currentTarget.value)}
                    />
                  </TextField.Root>
                </Flex>

                <Flex direction="column" gap="2" className="mb-4">
                  <Text size="2" weight="bold">Contact Method</Text>
                  <Flex gap="3">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="sms"
                        checked={contactMethod === 'sms'}
                        onChange={() => setContactMethod('sms')}
                      />
                      <Text size="2">SMS</Text>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="voice"
                        checked={contactMethod === 'voice'}
                        onChange={() => setContactMethod('voice')}
                      />
                      <Text size="2">Voice</Text>
                    </label>
                  </Flex>
                </Flex>

                <Button
                  variant="solid"
                  color="green"
                  onClick={handleTrigger}
                  disabled={triggerLoading || !newArbitraryNow}
                  style={{ width: '100%' }}
                >
                  {triggerLoading ? (
                    <><ReloadIcon className="animate-spin" /> Triggering...</>
                  ) : (
                    'Trigger Survey'
                  )}
                </Button>

                {/* Result Display */}
                {triggerResult && (
                  <Box className="mt-4 p-3" style={{
                    borderRadius: '6px',
                    backgroundColor: triggerResult.success ? 'var(--green-3)' : 'var(--red-3)',
                    border: `1px solid ${triggerResult.success ? 'var(--green-7)' : 'var(--red-7)'}`,
                  }}>
                    <Flex align="center" gap="2" className="mb-2">
                      {triggerResult.success ? <CheckCircledIcon color="green" /> : <CrossCircledIcon color="red" />}
                      <Text size="2" weight="bold" color={triggerResult.success ? 'green' : 'red'}>
                        {triggerResult.success ? 'Triggered Successfully' : 'Trigger Failed'}
                      </Text>
                    </Flex>
                    {triggerResult.error && (
                      <Flex direction="column" gap="2">
                        <Text size="2" color="red">{triggerResult.error}</Text>
                        {triggerResult.error.includes('already claimed') && (
                          <Button
                            variant="soft"
                            color="orange"
                            size="1"
                            onClick={handleClearClaimAndRetry}
                            disabled={triggerLoading}
                          >
                            {triggerLoading ? (
                              <><ReloadIcon className="animate-spin" /> Clearing &amp; Retrying...</>
                            ) : (
                              'Clear Claim & Retry'
                            )}
                          </Button>
                        )}
                      </Flex>
                    )}
                    {triggerResult.occurrence_id && (
                      <Text size="1" className="text-slate-600">
                        Occurrence ID: {triggerResult.occurrence_id}
                      </Text>
                    )}
                    {triggerResult.delivery_results && (
                      <Box className="mt-2">
                        {triggerResult.delivery_results.map((dr: any, i: number) => (
                          <Text key={i} size="1" className="text-slate-600">
                            {dr.method}: {dr.ok ? 'OK' : 'Failed'} {dr.external_id ? `(${dr.external_id})` : ''} {dr.error ? `- ${dr.error}` : ''}
                          </Text>
                        ))}
                      </Box>
                    )}
                    {triggerResult.occurrence_window && (
                      <Box className="mt-1">
                        <Text size="1" className="text-slate-500">
                          Window: {triggerResult.occurrence_window.release_at} to {triggerResult.occurrence_window.close_at}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Card>

        {/* PHA-406 V2: Survey Templates */}
        <Card className="mb-6">
          <Box p="4">
            <Flex align="center" justify="between" className="mb-4">
              <Heading size="4">New Patients survey scheduling default</Heading>
            </Flex>

            {/* Org selector */}
            <Flex gap="2" align="center" className="mb-4">
              <Text size="2" weight="bold">Organization:</Text>
              <select
                value={templatesOrgId || ''}
                onChange={(e) => e.target.value && loadSurveyTemplates(e.target.value)}
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--gray-6)', minWidth: 200, fontSize: '14px' }}
              >
                {orgs.map(org => (
                  <option key={org.id} value={String(org.id)}>{org.name || `Org ${org.id}`}</option>
                ))}
              </select>
              {templatesLoading && <ReloadIcon className="animate-spin" />}
            </Flex>

            {templatesOrgId && templatesLoading && (
              <Flex justify="center" align="center" style={{ minHeight: '80px' }}>
                <ReloadIcon className="animate-spin" width="24" height="24" />
              </Flex>
            )}

            {templatesOrgId && !templatesLoading && (
              <Box>
                {/* Template cards */}
                {templatesDraft.map((tmpl: any, idx: number) => (
                  <Card key={idx} className="mb-3 p-4" style={{ border: '1px solid var(--gray-5)' }}>
                    <Flex justify="between" align="center" className="mb-3">
                      <Text size="3" weight="bold">{getTemplateTitle(tmpl)}</Text>
                      <Button size="1" variant="ghost" color="red" onClick={() => {
                        setTemplatesDraft(prev => prev.filter((_: any, i: number) => i !== idx));
                      }}>
                        <TrashIcon /> Delete
                      </Button>
                    </Flex>

                    <Flex gap="3" align="center" className="mb-3">
                      <Text size="2">Cadence:</Text>
                      <select
                        value={tmpl.cadence_type}
                        onChange={(e) => {
                          const updated = [...templatesDraft];
                          updated[idx] = { ...updated[idx], cadence_type: e.target.value, cadence_day: e.target.value === 'weekly' ? 1 : null, cadence_time_hour: e.target.value === 'unscheduled' ? null : (updated[idx].cadence_time_hour ?? 9), cadence_time_minute: e.target.value === 'unscheduled' ? null : (updated[idx].cadence_time_minute ?? 0) };
                          setTemplatesDraft(updated);
                        }}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-6)', fontSize: '14px' }}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>

                      {tmpl.cadence_type === 'weekly' && (
                        <select
                          value={tmpl.cadence_day ?? 1}
                          onChange={(e) => {
                            const updated = [...templatesDraft];
                            updated[idx] = { ...updated[idx], cadence_day: parseInt(e.target.value) };
                            setTemplatesDraft(updated);
                          }}
                          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-6)', fontSize: '14px' }}
                        >
                          <option value="0">Sunday</option>
                          <option value="1">Monday</option>
                          <option value="2">Tuesday</option>
                          <option value="3">Wednesday</option>
                          <option value="4">Thursday</option>
                          <option value="5">Friday</option>
                          <option value="6">Saturday</option>
                        </select>
                      )}

                      {tmpl.cadence_type !== 'unscheduled' && (
                        <TimeInput12h
                          value={`${String(tmpl.cadence_time_hour ?? 9).padStart(2, '0')}:${String(tmpl.cadence_time_minute ?? 0).padStart(2, '0')}`}
                          onChange={(v) => {
                            const [h, m] = v.split(':');
                            const updated = [...templatesDraft];
                            updated[idx] = { ...updated[idx], cadence_time_hour: parseInt(h), cadence_time_minute: parseInt(m) };
                            setTemplatesDraft(updated);
                          }}
                        />
                      )}
                    </Flex>

                    <Text size="2" weight="medium" className="mb-2 block">Question Groups:</Text>
                    {ALL_GROUPS.map(group => {
                      const checked = (tmpl.question_groups || []).includes(group);
                      return (
                        <Flex key={group} gap="2" align="center" className="mb-1">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const updated = [...templatesDraft];
                              if (e.target.checked) {
                                // Add to this template, remove from others (1:1 constraint)
                                updated.forEach((t: any, i: number) => {
                                  if (i !== idx) {
                                    t.question_groups = (t.question_groups || []).filter((g: string) => g !== group);
                                  }
                                });
                                updated[idx] = { ...updated[idx], question_groups: [...(updated[idx].question_groups || []), group] };
                              } else {
                                updated[idx] = { ...updated[idx], question_groups: (updated[idx].question_groups || []).filter((g: string) => g !== group) };
                              }
                              setTemplatesDraft(updated);
                            }}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <Text size="2">{QUESTION_GROUP_LABELS[group]}</Text>
                        </Flex>
                      );
                    })}
                  </Card>
                ))}

                {/* Unscheduled card */}
                {(() => {
                  const assigned = new Set(templatesDraft.flatMap((t: any) => t.question_groups || []));
                  const unassigned = ALL_GROUPS.filter(g => !assigned.has(g));
                  return (
                    <Card className="mb-3 p-4" style={{ border: '1px dashed var(--gray-5)', background: 'var(--gray-2)' }}>
                      <Text size="3" weight="bold" color="gray" className="mb-2 block">On-Demand</Text>
                      {unassigned.length === 0 ? (
                        <Text size="2" color="gray">(No unassigned question groups)</Text>
                      ) : (
                        unassigned.map(group => (
                          <Text key={group} size="2" color="gray" className="block">{QUESTION_GROUP_LABELS[group]}</Text>
                        ))
                      )}
                    </Card>
                  );
                })()}

                {/* Add + Save buttons */}
                <Flex gap="3" justify="between" className="mt-3">
                  <Button variant="soft" onClick={() => {
                    setTemplatesDraft(prev => [...prev, { cadence_type: 'weekly', cadence_day: 1, cadence_time_hour: 9, cadence_time_minute: 0, question_groups: [], sort_order: prev.length }]);
                  }}>
                    <PlusIcon /> Add Survey
                  </Button>
                  <Button
                    size="2"
                    onClick={saveSurveyTemplates}
                    disabled={templatesEqual(templatesDraft, templatesOriginal) || templatesSaving}
                  >
                    {templatesSaving ? <><ReloadIcon className="animate-spin" /> Saving...</> : 'Save'}
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>
        </Card>

        {/* Organization Contact Exclusions */}
        <Card className="mb-6">
          <Box p="4">
            <Flex align="center" justify="between" className="mb-4">
              <Heading size="4">Organization Contact Exclusions</Heading>
              <Button variant="soft" onClick={loadOrganizations} disabled={orgsLoading}>
                <ReloadIcon className={orgsLoading ? 'animate-spin' : ''} />
                Reload Orgs
              </Button>
            </Flex>

            {orgsLoading && orgs.length === 0 ? (
              <Flex justify="center" align="center" style={{ minHeight: '120px' }}>
                <ReloadIcon className="animate-spin" width="24" height="24" />
              </Flex>
            ) : orgs.length === 0 ? (
              <Text color="gray">No active organizations found.</Text>
            ) : (
              <Table.Root variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell style={{ width: 40 }} />
                    <Table.ColumnHeaderCell>Org</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Owner</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Excluded Weekdays</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: 140 }}>Actions</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {orgs.map((org) => {
                    const expanded = expandedOrgIds.has(org.id);
                    const draftWeekdays = weekdaysDraftByOrg[org.id] || [];
                    const datesLoading = excludedDatesLoadingByOrg[org.id];
                    const excludedDates = excludedDatesByOrg[org.id] || [];
                    const addDraft = addExcludedDateDraftByOrg[org.id] || { exclusion: '', reason: '' };

                    return (
                      <React.Fragment key={org.id}>
                        <Table.Row>
                          <Table.Cell>
                            <Button
                              variant="ghost"
                              onClick={() => toggleOrgExpanded(org.id)}
                              aria-label={expanded ? 'Collapse' : 'Expand'}
                            >
                              {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            </Button>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2" weight="bold">{org.name || `Org ${org.id}`}</Text>
                            <Text size="1" color="gray" className="block">ID: {org.id}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2">{org.owner || '—'}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Flex gap="2" wrap="wrap">
                              {WEEKDAYS.map((day) => {
                                const selected = draftWeekdays.includes(day);
                                return (
                                  <Button
                                    key={day}
                                    size="1"
                                    variant={selected ? 'solid' : 'soft'}
                                    color={selected ? 'orange' : 'gray'}
                                    onClick={() => {
                                      setWeekdaysDraftByOrg(prev => {
                                        const curr = prev[org.id] || [];
                                        const next = curr.includes(day) ? curr.filter(d => d !== day) : [...curr, day];
                                        return { ...prev, [org.id]: next };
                                      });
                                    }}
                                  >
                                    {day}
                                  </Button>
                                );
                              })}
                            </Flex>
                          </Table.Cell>
                          <Table.Cell>
                            <Button variant="solid" onClick={() => saveWeekdays(org.id)}>
                              Save
                            </Button>
                          </Table.Cell>
                        </Table.Row>

                        {expanded && (
                          <Table.Row>
                            <Table.Cell colSpan={5}>
                              <Box style={{ padding: '12px 8px' }}>
                                <Heading size="3" className="mb-3">Excluded Date Ranges</Heading>

                                {datesLoading ? (
                                  <Flex justify="center" align="center" style={{ minHeight: '80px' }}>
                                    <ReloadIcon className="animate-spin" width="20" height="20" />
                                  </Flex>
                                ) : (
                                  <>
                                    <Table.Root variant="surface">
                                      <Table.Header>
                                        <Table.Row>
                                          <Table.ColumnHeaderCell>Exclusion (daterange text)</Table.ColumnHeaderCell>
                                          <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                                          <Table.ColumnHeaderCell style={{ width: 120 }}>Actions</Table.ColumnHeaderCell>
                                        </Table.Row>
                                      </Table.Header>
                                      <Table.Body>
                                        {excludedDates.length === 0 ? (
                                          <Table.Row>
                                            <Table.Cell colSpan={3}>
                                              <Text color="gray">No excluded date ranges for this organization.</Text>
                                            </Table.Cell>
                                          </Table.Row>
                                        ) : (
                                          excludedDates.map((row, idx) => (
                                            <Table.Row key={`${row.exclusion}-${idx}`}>
                                              <Table.Cell>
                                                <Badge color="gray">{row.exclusion}</Badge>
                                              </Table.Cell>
                                              <Table.Cell>
                                                <Text size="2">{row.reason || '—'}</Text>
                                              </Table.Cell>
                                              <Table.Cell>
                                                <Button
                                                  variant="soft"
                                                  color="red"
                                                  onClick={() => deleteExcludedDate(org.id, row.exclusion)}
                                                >
                                                  <TrashIcon />
                                                  Delete
                                                </Button>
                                              </Table.Cell>
                                            </Table.Row>
                                          ))
                                        )}

                                        <Table.Row>
                                          <Table.Cell>
                                            <TextField.Root>
                                              <TextField.Input
                                                placeholder="[2026-04-01,2026-04-10)"
                                                value={addDraft.exclusion}
                                                onChange={(e) =>
                                                  setAddExcludedDateDraftByOrg(prev => ({
                                                    ...prev,
                                                    [org.id]: { ...addDraft, exclusion: e.currentTarget.value },
                                                  }))
                                                }
                                              />
                                            </TextField.Root>
                                          </Table.Cell>
                                          <Table.Cell>
                                            <TextField.Root>
                                              <TextField.Input
                                                placeholder="Reason (optional)"
                                                value={addDraft.reason}
                                                onChange={(e) =>
                                                  setAddExcludedDateDraftByOrg(prev => ({
                                                    ...prev,
                                                    [org.id]: { ...addDraft, reason: e.currentTarget.value },
                                                  }))
                                                }
                                              />
                                            </TextField.Root>
                                          </Table.Cell>
                                          <Table.Cell>
                                            <Button variant="solid" onClick={() => addExcludedDate(org.id)}>
                                              <PlusIcon />
                                              Add
                                            </Button>
                                          </Table.Cell>
                                        </Table.Row>
                                      </Table.Body>
                                    </Table.Root>
                                  </>
                                )}
                              </Box>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            )}
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminScheduler;
